'use client';

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { api } from '@/lib/api';
import type { User as SupabaseUser } from '@supabase/supabase-js';
import { identifyUser, resetUser, isInternalUser, trackSignupCompleted } from '@/lib/posthog';
import { logger } from '@/lib/logger';
import { logError } from '@/lib/utils';
import { getSessionWithTimeout } from '@/lib/auth/session-utils';
import { getStoredToken, setStoredToken, removeStoredToken } from '@/lib/auth/token-utils';
import { extractErrorForLogging, isNetworkError } from '@/lib/utils/error-utils';
import { AUTH_CONSTANTS } from '@/lib/auth/constants';

/**
 * User type from auth context.
 * Contains user profile information including subscription and Gmail account details.
 */
export interface AuthUser {
  id: string;
  email: string;
  subscription_tier: string;
  subscription_status: string;
  trial_ends_at?: string | null;
  discount_percentage?: number | null;
  gmail_connected: boolean;
  // Server-computed HMAC-SHA256 signature over the user ID, used as Crisp's
  // verified `user:hash` identity claim. Null when CRISP_IDENTITY_KEY isn't
  // configured server-side — see lib/crisp-identity.ts.
  crisp_hash?: string | null;
  gmail_accounts: Array<{
    id: string;
    email: string;
    is_primary: boolean;
    credentials_invalid?: boolean;
    created_at: string;
  }>;
}

// Keep User as alias for backward compatibility
type User = AuthUser;

interface AuthContextType {
  user: User | null;
  loading: boolean;
  logout: () => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * A transient failure (request timeout or network error) does NOT mean the user
 * is unauthenticated — the session may still be perfectly valid, the backend was
 * just slow. Transient failures must never remove the stored token or clear an
 * already-authenticated user.
 */
function isTransientAuthFailure(message: string, errorInstance?: Error): boolean {
  // An authoritative rejection is never transient — check it first so a 401 whose
  // detail text happens to mention a timeout/connection cannot preserve a session
  // the server has actively rejected.
  if (isAuthoritativeRejection(message)) {
    return false;
  }
  const normalized = message.toLowerCase();
  // The ApiClient re-wraps timeout AND network failures as plain Error instances
  // (not TypeError) with these guaranteed messages, so we must classify on the
  // message text — `isNetworkError`'s `instanceof TypeError` check never matches
  // once an error has passed through the client's request() wrapper.
  if (
    normalized.includes('timed out') ||
    normalized.includes('timeout') ||
    normalized.includes('network request failed') ||
    normalized.includes('failed to fetch') ||
    normalized.includes('check your connection') ||
    normalized.includes('check your internet connection')
  ) {
    return true;
  }
  // Fallback for the rare case a raw TypeError reaches us unwrapped.
  return errorInstance ? isNetworkError(errorInstance) : false;
}

/**
 * An authoritative auth rejection (a 401 / Unauthorized response) is the only
 * signal that justifies dropping the stored token.
 */
function isAuthoritativeRejection(message: string): boolean {
  const normalized = message.toLowerCase();
  return normalized.includes('unauthorized') || normalized.includes('401');
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  /**
   * Fetches user profile from backend API.
   *
   * @param supabaseUser - Supabase user object (used for type compatibility, not actually used)
   * @returns User profile with subscription and Gmail account info, or null if fetch fails
   */
  const fetchUserProfile = async (supabaseUser: SupabaseUser): Promise<User | null> => {
    try {
      // Get user profile from backend
      const userData = await api.getCurrentUser();
      return userData;
    } catch (error) {
      const { message, errorInstance } = extractErrorForLogging(error);

      // Handle "Invalid data" errors (JSON parse failures) with more context
      // These often indicate HTML responses instead of JSON (e.g., 401 redirects)
      if (message === 'Invalid data' || (message.includes('Invalid data') && message.length < 50)) {
        logError(
          'Invalid data error while fetching user profile (likely HTML response instead of JSON)',
          {
            error: message,
            userId: supabaseUser.id,
            errorType: 'json_parse',
            hint: 'This may indicate an expired session or server error. The API may have returned HTML instead of JSON.',
          },
          errorInstance
        );
        // Return null to indicate no profile found (user should refresh or re-authenticate)
        return null;
      }

      // Distinguish between network errors, auth errors, and other errors
      if (errorInstance && isNetworkError(errorInstance)) {
        logError(
          'Network error while fetching user profile',
          {
            error: message,
            userId: supabaseUser.id,
            errorType: 'network',
          },
          errorInstance
        );
      } else if (message.includes('Unauthorized') || message.includes('401')) {
        // 401 errors are expected for expired tokens - log at debug level
        // The client will handle token refresh or sign-out automatically
        logger.debug('Token expired or invalid (expected)', {
          error: message,
          userId: supabaseUser.id,
          errorType: 'auth',
        });
      } else {
        logError(
          'Failed to fetch user profile',
          {
            error: message,
            userId: supabaseUser.id,
            errorType: 'api',
          },
          errorInstance
        );
      }

      // Re-throw so the caller can distinguish a transient failure (timeout /
      // network) from an authoritative auth rejection (401 / Unauthorized).
      // Swallowing every error here made a slow-but-valid request look identical
      // to "not authenticated", which logged valid users out.
      throw error;
    }
  };

  /**
   * Identifies user in PostHog with user profile data
   * Extracted to a helper function to avoid duplication
   */
  const identifyUserInPostHog = useCallback((userProfile: User) => {
    // Check if user is internal/test user (filter out from analytics)
    const isInternal = isInternalUser(userProfile.email);

    identifyUser(userProfile.id, {
      email: userProfile.email,
      subscription_tier: userProfile.subscription_tier,
      subscription_status: userProfile.subscription_status,
      gmail_connected: userProfile.gmail_connected,
      gmail_accounts_count: userProfile.gmail_accounts?.length || 0,
      is_internal_user: isInternal,
      // Add account age if we have created_at from gmail_accounts
      ...(userProfile.gmail_accounts?.[0]?.created_at && {
        first_gmail_connected_at: userProfile.gmail_accounts[0].created_at,
      }),
    });
  }, []);

  /**
   * Refreshes user authentication state using a dual-strategy approach:
   * 1. First tries localStorage token (fastest, most reliable)
   * 2. Falls back to Supabase session (slower, may be unavailable)
   *
   * This ensures auth works even if Supabase is slow or unavailable.
   */
  const refreshUser = useCallback(async () => {
    // Tracks whether a transient failure (timeout / network) occurred, so we
    // never eject an already-authenticated user on a slow-but-valid request.
    let transientFailure = false;

    try {
      // Strategy 1: Check localStorage first (fastest, most reliable)
      if (typeof window !== 'undefined') {
        const storedToken = getStoredToken();
        if (storedToken) {
          let profileTimeout: ReturnType<typeof setTimeout> | undefined;
          try {
            // fetchUserProfile calls api.getCurrentUser() which uses the token from localStorage
            // This is the most reliable method since we control the token storage.
            // Race against a timeout that REJECTS, so a hung request is surfaced
            // as a transient error (not an ambiguous null "no profile found").
            const userProfile = await Promise.race([
              fetchUserProfile({ id: 'fallback' } as SupabaseUser),
              new Promise<never>((_, reject) => {
                profileTimeout = setTimeout(
                  () => reject(new Error('User profile fetch timed out')),
                  AUTH_CONSTANTS.SESSION_REFRESH_TIMEOUT_MS
                );
              }),
            ]);
            if (userProfile) {
              setUser(userProfile);
              // Keep the PostHog person profile in sync so properties like
              // `email`, `gmail_connected`, and `gmail_accounts_count` are
              // updated after a Gmail connect or subscription change — not
              // just on the next SIGNED_IN event.
              identifyUserInPostHog(userProfile);
              setLoading(false);
              return;
            }
          } catch (tokenError) {
            const { message, errorInstance } = extractErrorForLogging(tokenError);
            if (isTransientAuthFailure(message, errorInstance)) {
              // Timeout / network error: the token is likely still valid.
              // Do NOT remove it and do NOT clear the user below.
              transientFailure = true;
              logger.debug(
                'User profile fetch timed out or hit a network error (preserving session)',
                { error: message }
              );
            } else {
              logger.warn(
                'Failed to fetch user profile with stored token',
                { error: message },
                errorInstance
              );
              // Only drop the token on an authoritative auth rejection (401).
              // A transient failure must never invalidate a valid token.
              if (isAuthoritativeRejection(message)) {
                removeStoredToken();
              }
            }
            // Continue to try the Supabase session below.
          } finally {
            if (profileTimeout) {
              clearTimeout(profileTimeout);
            }
          }
        }
      }

      // Strategy 2: Try Supabase session (may be slower or unavailable)
      const sessionResult = await getSessionWithTimeout(AUTH_CONSTANTS.SESSION_REFRESH_TIMEOUT_MS);
      const session = sessionResult.data?.session;
      const error = sessionResult.error;

      // If we have a Supabase session, use it
      if (!error && session?.user) {
        try {
          const userProfile = await fetchUserProfile(session.user);
          if (userProfile) {
            setUser(userProfile);
            // Keep the PostHog person profile in sync here too — refreshUser
            // is the fallback path that runs on initial load and on
            // reconnects, so identifying here ensures the person profile is
            // always up to date with the latest `email`, `gmail_connected`,
            // etc.
            identifyUserInPostHog(userProfile);
            // Sync token to localStorage for consistency
            if (session.access_token) {
              setStoredToken(session.access_token);
            }
            setLoading(false);
            return;
          }
        } catch (profileError) {
          const { message, errorInstance } = extractErrorForLogging(profileError);
          if (isTransientAuthFailure(message, errorInstance)) {
            transientFailure = true;
          }
          logError(
            'Failed to fetch user profile from Supabase session',
            {
              error: message,
              userId: session.user.id,
            },
            errorInstance
          );
        }
      }

      // Log Supabase check failure if there was an error
      if (error) {
        const { message, errorInstance } = extractErrorForLogging(error);
        if (isTransientAuthFailure(message, errorInstance)) {
          transientFailure = true;
        }
        logger.warn('Supabase session check failed', { error: message }, errorInstance);
      }

      // No valid session or token found. A transient timeout/network failure must
      // NOT eject an already-authenticated user — only clear the user for an
      // authoritative "not authenticated" outcome.
      setUser((prev) => (transientFailure ? prev : null));
      setLoading(false);
    } catch (error) {
      const { message, errorInstance } = extractErrorForLogging(error);
      logError('Failed to refresh user', { error: message }, errorInstance);
      // Preserve the current user on transient failures; otherwise assume no session.
      const transient = transientFailure || isTransientAuthFailure(message, errorInstance);
      setUser((prev) => (transient ? prev : null));
      setLoading(false);
    }
  }, [identifyUserInPostHog]);

  useEffect(() => {
    let mounted = true;
    let subscription: { unsubscribe: () => void } | null = null;
    let timeoutId: NodeJS.Timeout | null = null;
    let userLoaded = false;

    const initAuth = async () => {
      // Set a maximum timeout for initialization - give more time for network requests
      // Increased to 5 seconds to allow for slower connections
      timeoutId = setTimeout(() => {
        if (mounted && !userLoaded) {
          // Stop the loading spinner if init is taking too long, but do NOT
          // force a logout: a slow refresh under load must not eject an
          // already-authenticated user.
          setLoading(false);
        }
      }, AUTH_CONSTANTS.INIT_TIMEOUT_MS);

      try {
        await refreshUser();
        userLoaded = true;
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
      } catch (error) {
        const { message, errorInstance } = extractErrorForLogging(error);
        logError('Failed to initialize auth', { error: message }, errorInstance);
        if (mounted) {
          setUser(null);
          setLoading(false);
        }
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
      }
    };

    // Execute initAuth and handle any unhandled rejections
    initAuth().catch((error) => {
      // This catch ensures the promise is always handled, even if there's an error
      // that escapes the try-catch inside initAuth (e.g., in setTimeout callbacks)
      const { message, errorInstance } = extractErrorForLogging(error);
      logError('Unhandled error in initAuth', { error: message }, errorInstance);
      if (mounted) {
        setUser(null);
        setLoading(false);
      }
    });

    // Listen for auth state changes
    try {
      const { data } = supabase.auth.onAuthStateChange((event, session) => {
        if (!mounted) return;

        /**
         * Handles authentication state changes from Supabase.
         * Wraps async operations in a promise with proper error handling
         * to prevent unhandled promise rejections.
         *
         * @internal - Internal helper function, not exported
         */
        const handleAuthStateChange = async () => {
          // Handle token refresh - sync new token to localStorage
          if (event === 'TOKEN_REFRESHED' && session?.access_token) {
            setStoredToken(session.access_token);
            logger.debug('Token refreshed and synced to localStorage');
            return;
          }

          if (event === 'SIGNED_IN' && session?.user) {
            // Sync token to localStorage on sign in
            if (session.access_token) {
              setStoredToken(session.access_token);
            }

            try {
              const userProfile = await fetchUserProfile(session.user);
              if (!mounted) return; // Check if component is still mounted

              setUser(userProfile);
              // Identify user in PostHog with enhanced properties
              if (userProfile) {
                identifyUserInPostHog(userProfile);

                // Detect first-time signup: fire `signup_completed` when the
                // Supabase user was created within the last 60 seconds. Keeps
                // the funnel between `login_initiated` and `gmail_connected`
                // measurable without an extra DB round-trip.
                const createdAtIso = session.user.created_at;
                if (createdAtIso) {
                  const ageMs = Date.now() - new Date(createdAtIso).getTime();
                  if (Number.isFinite(ageMs) && ageMs >= 0 && ageMs < 60_000) {
                    trackSignupCompleted('google');
                  }
                }
              }
            } catch (error) {
              if (!mounted) return; // Check if component is still mounted
              const { message, errorInstance } = extractErrorForLogging(error);
              logError(
                'Failed to fetch user profile on sign in',
                {
                  error: message,
                  userId: session.user.id,
                },
                errorInstance
              );
              // supabase-js re-emits SIGNED_IN on session restore / tab refocus, not
              // only on an explicit login. A transient timeout/network failure here
              // must not eject an already-authenticated user — only clear on an
              // authoritative outcome.
              setUser((prev) => (isTransientAuthFailure(message, errorInstance) ? prev : null));
            } finally {
              if (mounted) {
                setLoading(false);
              }
            }
          } else if (event === 'SIGNED_OUT') {
            if (!mounted) return;
            resetUser();
            setUser(null);
            setLoading(false);
          }
        };

        // Execute and catch any unhandled rejections
        handleAuthStateChange().catch((error) => {
          // This catch ensures the promise is always handled, even if there's an error
          // that escapes the try-catch inside handleAuthStateChange
          if (!mounted) return;

          const { message, errorInstance } = extractErrorForLogging(error);
          logError(
            'Unhandled error in auth state change handler',
            { error: message, event },
            errorInstance
          );
          setUser(null);
          setLoading(false);
        });
      });
      subscription = data.subscription;
    } catch (error) {
      const { message, errorInstance } = extractErrorForLogging(error);
      logError('Failed to set up auth state listener', { error: message }, errorInstance);
      // If we can't set up the listener, at least ensure loading is false
      if (mounted) {
        setLoading(false);
      }
    }

    return () => {
      mounted = false;
      if (subscription) {
        subscription.unsubscribe();
      }
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [refreshUser]);

  const logout = async () => {
    // Reset PostHog user identification
    resetUser();
    // Clear token from localStorage
    removeStoredToken();
    await supabase.auth.signOut();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
